import React, { useState, useEffect, useRef } from 'react';
import { ConstructorEventUpdateSQLStatus } from "../../../../Event/event";

const FunMathQuizGame = () => {
  const [score, setScore] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [feedbackColor, setFeedbackColor] = useState('');
  const [confettiElements, setConfettiElements] = useState([]);
  const [answerValue, setAnswerValue] = useState('');
  const inputRef = useRef(null); // Ref to focus input

  const questions = [
    { q: "What number comes before 103?", a: 102 },
    { q: "Skip count by 4s: 4, 8, ___, ___, ___", a: "12,16,20" },
    { q: "Double 17", a: 34 },
    { q: "Half of 48", a: 24 },
    { q: "What is the value of 2 in 2,341?", a: 2000 },
    { q: "Round 67 to the nearest 10", a: 70 },
    { q: "Estimate: 392 + 208 ≈ ?", a: 600 },
    { q: "What is 10 more than 89?", a: 99 },
    { q: "Double 45 then subtract 10", a: 80 },
    { q: "Half of 96 + 2", a: 50 },
    { q: "What is the value of the 3 in 1,385?", a: 300 },
    { q: "Round 4,645 to the nearest 1,000", a: 5000 },
    { q: "Estimate: 798 + 202 = ?", a: 1000 },
    { q: "Add: 60 + 90 (group tens)", a: 150 },
    { q: "Add: 430 + 270 (left-to-right)", a: 700 },
    { q: "58 + 25 (use 50+20 and 8+5)", a: 83 },
    { q: "What is 100 – 43? (complement to 100)", a: 57 },
    { q: "What is 100 – 64? (complement strategy)", a: 36 },
    { q: "Subtract 1000 – 247 (front-end trick)", a: 753 },
    { q: "Subtract 1000 – 183", a: 817 },
    { q: "Skip count by 6s: 6, 12, ___, ___, ___", a: "18,24,30" },
    { q: "Double 38 then add 4", a: 80 },
    { q: "Half of 72 then subtract 2", a: 34 },
    { q: "Round 749 to the nearest 100", a: 700 },
    { q: "Round 2,184 to the nearest 1,000", a: 2000 },
    { q: "Estimate: 645 + 310 = ?", a: 955 },
    { q: "Add: 360 + 240 (group hundreds and tens)", a: 600 },
    { q: "Backtrack: 92 – 37 = ?", a: 55 },
    { q: "Subtract: 1000 – 509 = ?", a: 491 },
    { q: "What is the value of the 8 in 8,194?", a: 8000 }
  ];

  const normalizeAnswer = (input) => input.trim().toLowerCase().replace(/\s+/g, '');

  const checkAnswer = (e) => {
    e.preventDefault();
    const userAnswerRaw = answerValue;
    const correctAnswer = questions[currentQuestionIndex].a;
    let isCorrect = false;

    if (typeof correctAnswer === 'number') {
      isCorrect = Number(userAnswerRaw) === correctAnswer;
    } else {
      isCorrect = normalizeAnswer(userAnswerRaw) === normalizeAnswer(correctAnswer);
    }

    if (isCorrect) {
      setScore((prev) => prev + 1);
      setFeedback('🎉 Correct!');
      setFeedbackColor('#2e7d32');
      showConfetti();
    } else {
      setFeedback(`❌ Wrong! The correct answer is: ${correctAnswer}`);
      setFeedbackColor('#c62828');
    }

    // Clear input right now
    setAnswerValue('');

    // Move to next question after delay
    setTimeout(() => {
      setCurrentQuestionIndex((prev) => prev + 1);
      setFeedback('');
    }, 2000);
  };

  const showConfetti = () => {
    const elements = [];
    for (let i = 0; i < 30; i++) {
      elements.push(
        <div
          key={i + Math.random()}
          style={{
            position: 'fixed',
            width: '10px',
            height: '10px',
            backgroundColor: `hsl(${Math.floor(Math.random() * 360)}, 70%, 60%)`,
            opacity: 0.8,
            pointerEvents: 'none',
            animation: `fall ${2 + Math.random() * 2}s linear forwards`,
            left: Math.random() * window.innerWidth + 'px'
          }}
        ></div>
      );
    }
    setConfettiElements(elements);
    setTimeout(() => setConfettiElements([]), 3000);
  };

  const question = questions[currentQuestionIndex];

  // Ensure input is cleared and focused when question changes
  useEffect(() => {
    setAnswerValue('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [currentQuestionIndex]);

  useEffect(() => {
    if (currentQuestionIndex === questions.length) {
      const responsedata = ConstructorEventUpdateSQLStatus();
      console.log("Event completed", responsedata);
    }
  }, [currentQuestionIndex]);

  return (
    <div style={{
      fontFamily: 'Poppins, sans-serif',
      backgroundImage: 'url("https://static.vecteezy.com/system/resources/thumbnails/021/826/876/small_2x/hand-drawn-math-symbols-icon-in-flat-style-mathematics-formula-illustration-on-isolated-background-school-education-sign-business-concept-vector.jpg")',
      minHeight: '100vh',
      overflowX: 'hidden',
      padding: '1rem'
    }}>
      {confettiElements}
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <div style={{
          background: '#fff',
          border: '3px solid #4a148c',
          borderRadius: '30px',
          padding: '5vw',
          maxWidth: '500px',
          width: '100%',
          textAlign: 'center'
        }}>
          <h2 style={{
            fontSize: '1.8rem',
            color: '#4a148c',
            fontFamily: 'Comic Neue, cursive',
            textShadow: '1px 1px 3px #b39ddb'
          }}>
            Score → <span>{score}</span>
          </h2>
          <h3 style={{
            fontSize: '1.2rem',
            margin: '20px 0 30px',
            minHeight: '3em'
          }}>
            {question ? question.q : `🎉 Quiz Complete! Your final score is ${score} / ${questions.length}`}
          </h3>
          {question && (
            <form onSubmit={checkAnswer} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <input
                ref={inputRef}
                name="answer"
                type="text"
                required
                autoComplete="off"
                inputMode="numeric"
                value={answerValue}
                onChange={(e) => setAnswerValue(e.target.value)}
                style={{
                  fontSize: '1.2rem',
                  padding: '1rem',
                  border: '2px solid #4a148c',
                  borderRadius: '15px',
                  textAlign: 'center'
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginTop: '1rem' }}>
  <button
    type="button"
    onClick={() => {
      setCurrentQuestionIndex((prev) => Math.max(prev - 1, 0));
      setFeedback('');
    }}
    style={{
      flex: 1,
      padding: '1rem',
      backgroundColor: '#6a1b9a',
      color: '#fff',
      fontSize: '1rem',
      fontWeight: 700,
      border: 'none',
      borderRadius: '15px',
      cursor: currentQuestionIndex === 0 ? 'not-allowed' : 'pointer',
      opacity: currentQuestionIndex === 0 ? 0.5 : 1,
    }}
    disabled={currentQuestionIndex === 0}
  >
    Previous
  </button>

  <button
    type="button"
    onClick={() => {
      setCurrentQuestionIndex((prev) =>
        Math.min(prev + 1, questions.length - 1)
      );
      setFeedback('');
    }}
    style={{
      flex: 1,
      padding: '1rem',
      backgroundColor: '#4a148c',
      color: '#fff',
      fontSize: '1rem',
      fontWeight: 700,
      border: 'none',
      borderRadius: '15px',
      cursor:
        currentQuestionIndex === questions.length - 1 ? 'not-allowed' : 'pointer',
      opacity: currentQuestionIndex === questions.length - 1 ? 0.5 : 1,
    }}
    disabled={currentQuestionIndex === questions.length - 1}
  >
    Next
  </button>
</div>

              <button
                type="submit"
                style={{
                  padding: '1rem',
                  backgroundColor: '#4a148c',
                  color: '#fff',
                  fontSize: '1.4rem',
                  fontWeight: 700,
                  border: 'none',
                  borderRadius: '15px',
                  cursor: 'pointer'
                }}
              >
                Submit
              </button>
            </form>
          )}
          <div style={{
            fontSize: '1rem',
            marginTop: '15px',
            height: '30px',
            fontWeight: 700,
            fontFamily: 'Comic Neue, cursive',
            color: feedbackColor
          }}>
            {feedback}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes fall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(600px) rotate(360deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default FunMathQuizGame;
